import React, { useEffect, useRef, useState } from 'react';
import investorApi from '/src/apis/investorApi';
import { ALLOWED_QUERY_OPERATOR, MAX_FUNDS_PER_MANAGER } from '/src/lib/constants';

const MAX_VISIBLE_FUNDS = 4;
const MID_DESKTOP_BREAKPOINT = 1500;
const BALANCE_SHEET_FUND_TYPE = 1;

const getVisibleFundsByViewport = () => {
    if (typeof window === 'undefined') {
        return MAX_VISIBLE_FUNDS;
    }

    return window.innerWidth <= MID_DESKTOP_BREAKPOINT ? 3 : MAX_VISIBLE_FUNDS;
};

const InvestmentFundTabs = (props) => {
    const {
        getFunding,
        hasDirectoryAccess = false,
        entityId,
        entityType,
        updateParentState,
        userSelectedFilters = [],
        investmentFundTabFilters = [],
        setInvestmentFundTabFilters,
        investmentStats = {},
        onDirectoryFundFilterChange = () => {},
        setAggregationFundFilters,
        requestedFundId = null,
        onFundIdConsumed = null,
    } = props;

    const [isOverflowOpen, setIsOverflowOpen] = useState(false);
    const [activeFundId, setActiveFundId] = useState('all');
    const [maxVisibleFunds, setMaxVisibleFunds] = useState(getVisibleFundsByViewport);
    const [allInvestmentCount, setAllInvestmentCount] = useState(Number(investmentStats?.numberOfInvestments || 0));
    const [fundOptions, setFundOptions] = useState([]);
    // The profile ("TYPE:id") whose funds the tabs hold, so a fund link waits while another profile's funds load
    const [loadedFundsFor, setLoadedFundsFor] = useState(null);
    const fundsKey = `${entityType}:${entityId}`;
    const [hasFundsError, setHasFundsError] = useState(false);

    const [othersOption, setOthersOption] = useState(null);

    const overflowRef = useRef(null);

    useEffect(() => {
        if (!requestedFundId) return;
        // Without directory access there are no fund tabs to select, so the request is consumed as it is
        if (!hasDirectoryAccess) {
            onFundIdConsumed?.();
            return;
        }
        if (loadedFundsFor !== fundsKey) return;

        // Direct match for regular funds
        let match = fundOptions.find(
            (f) => String(f.id) === String(requestedFundId)
        );

        // Balance-sheet funds are merged into a single synthetic tab — check fund_ids
        if (!match) {
            const balanceSheetTab = fundOptions.find((f) => f.id === 'balance-sheet');
            if (balanceSheetTab?.fund_ids?.includes(String(requestedFundId))) {
                match = balanceSheetTab;
            }
        }

        // A fund that is not in the list (e.g. added after the tabs loaded) falls back to "All funds"; the request is always
        // consumed so clicking the same fund again still triggers a selection
        if (match) {
            handleOnTabSelect(match.investment_ids, match.id, match);
        } else {
            handleOnTabSelect('all', 'all');
        }
        onFundIdConsumed?.();
    }, [requestedFundId, fundOptions, loadedFundsFor, fundsKey, hasDirectoryAccess]);

    useEffect(() => {
        const loadFundsData = async () => {
            if (!hasDirectoryAccess) {
                return;
            }

            setHasFundsError(false);

            let tempTotalFund = null;
            try {
                tempTotalFund = await fetchFundInvestment(isCurrent);
            } catch (error) {
                if (!isCurrent()) return;
                // No fund tabs without the fund list: "All funds" still works and a pending fund link falls back to it
                console.error('Failed to load the fund tabs', error);
                setFundOptions([]);
                setHasFundsError(true);
                setLoadedFundsFor(fundsKey);
            }
            // A response for a profile the user has already left is dropped, so it cannot replace the current tabs
            if (!isCurrent()) return;
            const totalInvestmentCount = Number(investmentStats?.numberOfInvestments || 0);

            const totalNonFundInvestments = tempTotalFund === null ? 0 : Math.max(totalInvestmentCount - tempTotalFund, 0);

            if (totalNonFundInvestments > 0 && totalNonFundInvestments !== totalInvestmentCount) {
                setOthersOption({
                    id: 'others',
                    name: 'Others',
                    company_investment_count: totalNonFundInvestments,
                    investment_ids: 'others'
                });
            } else {
                setOthersOption(null);
            }

            if (totalInvestmentCount > 0) {
                handleOnTabSelect('all', 'all');
            }
        };

        let isLoadCurrent = true;
        const isCurrent = () => isLoadCurrent;
        loadFundsData();
        return () => { isLoadCurrent = false; };
    }, [hasDirectoryAccess, entityId, entityType, investmentStats?.numberOfInvestments]);

    useEffect(() => {
        setAllInvestmentCount(Number(investmentStats?.numberOfInvestments || 0));
    }, [investmentStats?.numberOfInvestments]);

    useEffect(() => {
        // this effect fetches the funding data based on the selected fund tab.
        // It listens to changes in investmentFundTabFilters which is updated when a fund tab is selected.
        updateParentState({ isLoading: true });
        getFunding(userSelectedFilters, true);
    }, [investmentFundTabFilters])

    useEffect(() => {
        if (!isOverflowOpen) {
            return undefined;
        }

        const handleOutsideClick = (event) => {
            if (overflowRef.current && !overflowRef.current.contains(event.target)) {
                setIsOverflowOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isOverflowOpen]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleResize = () => {
            setMaxVisibleFunds(getVisibleFundsByViewport());
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // isCurrent: false once the profile changed while this request was in flight; such a response sets no state
    const fetchFundInvestment = async (isCurrent = () => true) => {
        const fields = ["id", "name", "investment_ids", "is_balance_sheet", "company_investment_count"];
        const resFundData = await investorApi.getFundsForInvestor(entityId, entityType, fields, MAX_FUNDS_PER_MANAGER, 1);
        if (!isCurrent()) return null;
        // Every fund gets a tab, even one with a single or no direct investment (its tab then shows "No investments");
        // the count is coerced once so the badges and the "Others" total below add numbers
        const fundsInvestmentData = (resFundData.data || [])
            .map((fund) => ({ ...fund, company_investment_count: Number(fund.company_investment_count || 0) }));
        const balanceSheetFunds = fundsInvestmentData.filter((fund) => fund.is_balance_sheet === BALANCE_SHEET_FUND_TYPE);
        const nonBalanceSheetFunds = fundsInvestmentData.filter((fund) => fund.is_balance_sheet !== BALANCE_SHEET_FUND_TYPE);

        const mergedFunds = [...nonBalanceSheetFunds];
        if (balanceSheetFunds.length > 0) {
            const mergedIds = balanceSheetFunds.flatMap((fund) => (Array.isArray(fund.investment_ids) ? fund.investment_ids : (fund.investment_ids != null ? [fund.investment_ids] : [])));
            const balanceSheetFundIds = balanceSheetFunds
                .map((fund) => fund.id)
                .filter((fundId) => fundId !== undefined && fundId !== null);

            mergedFunds.push({
                id: 'balance-sheet',
                name: 'Balance sheet',
                is_balance_sheet: BALANCE_SHEET_FUND_TYPE,
                company_investment_count: balanceSheetFunds.reduce((acc, fund) => acc + fund.company_investment_count, 0),
                investment_ids: mergedIds,
                fund_ids: balanceSheetFunds.map((fund) => String(fund.id)),
            });
        }

        const tempTotalFund = fundsInvestmentData.reduce((acc, fund) => acc + fund.company_investment_count, 0);
        setFundOptions(mergedFunds);
        setLoadedFundsFor(fundsKey);

        return tempTotalFund;
    };

    const handleOnTabSelect = (investmentIds, selectedFundId, selectedFund = null) => {
        if (selectedFundId === activeFundId) return;

        setActiveFundId(selectedFundId);

        if (selectedFundId === 'all') {
            onDirectoryFundFilterChange(null);
        } else if (selectedFundId === 'others') {
            onDirectoryFundFilterChange(['null']);
        } else if (selectedFundId === 'balance-sheet') {
            const fundIds = selectedFund?.fund_ids || [];
            onDirectoryFundFilterChange(fundIds.length > 0 ? fundIds : null);
        } else {
            const selectedId = selectedFund?.id;
            onDirectoryFundFilterChange(selectedId ? [selectedId] : null);
        }

        if (investmentIds === 'others') {
            setInvestmentFundTabFilters([
                { key: "fund_id", value: ['null'], op: ALLOWED_QUERY_OPERATOR.equal },
            ]);
            setAggregationFundFilters?.([
                { key: "fund_id", value: ['null'], op: ALLOWED_QUERY_OPERATOR.equal },
            ]);
        } else if (investmentIds === 'all') {
            setInvestmentFundTabFilters([]);
            setAggregationFundFilters?.([]);
        } else if (Array.isArray(investmentIds) && investmentIds.length > 0) {
            setInvestmentFundTabFilters([
                { key: "id", value: investmentIds, op: ALLOWED_QUERY_OPERATOR.equal },
            ]);
            // Balance sheet merges multiple funds — use their real IDs; otherwise use the single fund ID.
            const aggFundIds = selectedFund?.fund_ids?.length ? selectedFund.fund_ids : [String(selectedFundId)];
            setAggregationFundFilters?.([
                { key: "fund_id", value: aggFundIds, op: ALLOWED_QUERY_OPERATOR.equal },
            ]);
        } else {
            // Fund has 0 investments (empty or null investment_ids) — clear rounds immediately
            updateParentState({ rounds: [], totalRecords: 0, isLoading: false });
            // Use a non-empty filter so FundingView shows the "No investments" message
            setInvestmentFundTabFilters([
                { key: "id", value: [0], op: ALLOWED_QUERY_OPERATOR.equal },
            ]);
            setAggregationFundFilters?.([
                { key: "fund_id", value: [String(selectedFundId)], op: ALLOWED_QUERY_OPERATOR.equal },
            ]);
        }
    };

    const isBalanceSheet = (fund) => fund.is_balance_sheet === BALANCE_SHEET_FUND_TYPE;

    const orderedFundOptions = [...fundOptions].sort((a, b) => {
        const aIsBS = isBalanceSheet(a);
        const bIsBS = isBalanceSheet(b);
        if (aIsBS !== bIsBS) return aIsBS ? -1 : 1;
        return (b.company_investment_count || 0) - (a.company_investment_count || 0);
    });

    const visibleFundOptions = orderedFundOptions.slice(0, maxVisibleFunds);
    const overflowFundOptions = orderedFundOptions.slice(maxVisibleFunds);
    const selectedOverflowFund = overflowFundOptions.find(
        (fund) => String(fund.id) === String(activeFundId)
    );
    const isOverflowSelected = overflowFundOptions.some(
        (fund) => String(fund.id) === String(activeFundId)
    );
    const overflowTriggerLabel = selectedOverflowFund
        ? (isBalanceSheet(selectedOverflowFund) ? 'Balance sheet' : selectedOverflowFund.name)
        : `+${overflowFundOptions.length} funds`;
    const overflowTriggerCount = selectedOverflowFund?.company_investment_count;

    const renderFundChip = (fund, extraClassName = '') => (
        <button
            key={fund.id}
            type="button"
            className={[
                'investment-fund-tabs__chip',
                isBalanceSheet(fund) && 'investment-fund-tabs__chip--balance-sheet',
                String(activeFundId) === String(fund.id) && 'investment-fund-tabs__chip--active',
                extraClassName,
            ].filter(Boolean).join(' ')}
            onClick={() => {
                setIsOverflowOpen(false);
                handleOnTabSelect(fund.investment_ids, fund.id, fund);
            }}
        >
            {isBalanceSheet(fund) && <i className="fa fa-table" />}
            <span>{isBalanceSheet(fund) ? 'Balance sheet' : fund.name}</span>
            <span className="investment-fund-tabs__badge">{fund.company_investment_count}</span>
        </button>
    );

    const showAllFundsTab = allInvestmentCount > 0;

    return (
        <div className="investment-fund-tabs">
            <div className="investment-fund-tabs__list">
                {showAllFundsTab && (
                    <button
                        type="button"
                        className={[
                            'investment-fund-tabs__chip',
                            allInvestmentCount === 0 && 'investment-fund-tabs__chip--disabled',
                            String(activeFundId) === 'all' && 'investment-fund-tabs__chip--active',
                        ].filter(Boolean).join(' ')}
                        disabled={allInvestmentCount === 0}
                        onClick={() => {
                            if (allInvestmentCount === 0) {
                                return;
                            }
                            setIsOverflowOpen(false);
                            handleOnTabSelect('all', 'all');
                        }}
                    >
                        <span>All funds</span>
                        <span className="investment-fund-tabs__badge">{allInvestmentCount}</span>
                    </button>
                )}

                {visibleFundOptions.map((fund) => renderFundChip(fund))}

                {overflowFundOptions.length > 0 && (
                    <div
                        ref={overflowRef}
                        className="investment-fund-tabs__overflow"
                    >
                        <button
                            type="button"
                            className={[
                                'investment-fund-tabs__chip',
                                'investment-fund-tabs__chip--overflow',
                                isOverflowOpen && 'investment-fund-tabs__chip--open',
                                isOverflowSelected && 'investment-fund-tabs__chip--active',
                            ].filter(Boolean).join(' ')}
                            onClick={() => setIsOverflowOpen((prevState) => !prevState)}
                        >
                            <span>{overflowTriggerLabel}</span>
                            {selectedOverflowFund && (
                                <span className="investment-fund-tabs__badge">{overflowTriggerCount}</span>
                            )}
                            <i className={`fa ${isOverflowOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                        </button>

                        {isOverflowOpen && (
                            <div className="investment-fund-tabs__dropdown">
                                {overflowFundOptions.map((fund) => renderFundChip(fund))}
                            </div>
                        )}
                    </div>
                )}

                {othersOption?.company_investment_count > 0 && renderFundChip(othersOption, 'investment-fund-tabs__chip--others')}

                {hasFundsError && (
                    <p className="investment-fund-tabs__error">Fund tabs could not be loaded, please try again later.</p>
                )}
            </div>

        </div>
    );
};

export default InvestmentFundTabs;
